import { useState } from "react";

import { LandingSection } from "./LandingPrimitives";

type FaqItem = {
  question: string;
  answer: string;
};

/**
 * Visible FAQ on the landing.
 *
 * IMPORTANT: keep questions and answers in sync with the FAQPage JSON-LD
 * in `index.html`. Google verifies that the schema matches the visible
 * content before granting rich results.
 */
const faqItems: FaqItem[] = [
  {
    question: "Foaia de parcurs este obligatorie?",
    answer:
      "Da, dacă vrei să deduci integral (100%) cheltuielile și TVA-ul aferent vehiculelor. Pentru deducerea limitată la 50% nu este obligatorie, dar pierzi jumătate din deducerea fiscală.",
  },
  {
    question: "Trebuie făcută zilnic sau e suficient lunar?",
    answer:
      "Legea nu impune o frecvență. O singură foaie pe lună per vehicul, cu toate deplasările într-un tabel, este perfect legală. Majoritatea firmelor optează pentru lunar.",
  },
  {
    question: "Pot folosi un format electronic, PDF sau Excel?",
    answer:
      "Da. Legislația acceptă orice format, atât timp cât foaia conține informațiile minime cerute și poate fi prezentată la cerere — pe hârtie, ecran sau printată. Foile generate dintr-o aplicație au aceeași valoare ca cele scrise de mână.",
  },
  {
    question: "Ce trebuie să conțină o foaie de parcurs ca să fie acceptată la ANAF?",
    answer:
      "Minimum patru elemente, conform pct. 68 alin. (2) din normele Codului Fiscal: categoria vehiculului, scopul și locul deplasării, kilometrii parcurși și norma proprie de consum carburant pe kilometru. Restul informațiilor (șofer, alimentări, semnătură) sunt recomandate, dar nu obligatorii.",
  },
  {
    question: "PFA-urile trebuie să facă foi de parcurs?",
    answer:
      "Da, dacă PFA-ul folosește un autovehicul în activitatea economică și vrea să deducă integral cheltuielile. Regulile sunt identice cu cele pentru firme: foaie de parcurs obligatorie pentru deducere 100%, opțională pentru 50%.",
  },
  {
    question: "Care este diferența dintre deducere 50% și 100%?",
    answer:
      "Deducerea de 50% se aplică automat la vehicule sub 3,5 tone folosite mixt — fără foaie de parcurs. Deducerea de 100% se aplică doar pentru vehicule utilizate exclusiv în scop economic, dovedit prin foaie de parcurs corectă. Diferența pentru o firmă cu 3 vehicule poate depăși 7.000 lei pe an.",
  },
  {
    question: "Cât timp trebuie păstrate foile de parcurs?",
    answer:
      "Foile sunt documente justificative contabile și se păstrează minimum 5 ani de la data întocmirii, conform Legii contabilității. Recomandarea practică este 10 ani, pentru acoperirea termenului general de prescripție fiscală.",
  },
  {
    question: "Există un model obligatoriu impus de ANAF?",
    answer:
      "Nu. ANAF nu impune un formular tipizat. Ordinul ANRSPS 1296/2021 conține un model normativ pentru sectorul public, dar firmele private pot folosi propriul format — carnet preimprimat, Excel sau aplicație online — atât timp cât conține cele 4 informații obligatorii.",
  },
];

export default function LandingFaqSection() {
  const [openIdx, setOpenIdx] = useState<number | null>(0);

  return (
    <LandingSection
      id="intrebari-frecvente"
      eyebrow="Întrebări frecvente"
      title="Foile de parcurs, pe scurt"
      description="Răspunsuri la întrebările pe care le primim cel mai des — de la cerințele ANAF la diferența între deducere 50% și 100%."
    >
      <div className="landing-faq-list">
        {faqItems.map((item, idx) => {
          const open = openIdx === idx;
          return (
            <div
              key={item.question}
              className={`landing-faq-item ${open ? "landing-faq-item--open" : ""}`}
            >
              <button
                type="button"
                className="landing-faq-item__trigger"
                aria-expanded={open}
                onClick={() => setOpenIdx(open ? null : idx)}
              >
                <span>{item.question}</span>
                <span
                  className="landing-faq-item__icon"
                  aria-hidden="true"
                >
                  {open ? "−" : "+"}
                </span>
              </button>
              {open ? (
                <div className="landing-faq-item__answer">{item.answer}</div>
              ) : null}
            </div>
          );
        })}
      </div>
    </LandingSection>
  );
}
